// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { all, call, put, select, takeLatest, takeEvery, getContext } from "redux-saga/effects";
import { Store } from "@reduxjs/toolkit";
import { Base64 } from "js-base64";
import {
  ConfirmSignInOutput,
  FetchUserAttributesOutput,
  GetCurrentUserOutput,
  ResendSignUpCodeOutput,
  ResetPasswordOutput,
  SignInOutput,
  SignUpOutput,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { Amplify } from "aws-amplify";

import { GraphQLResult, RootPath, MainPanel, UserStateAWSError } from "types";
import { ChargerConnectionType, IO_TREND_LIMIT, eRfidLimitPeriod } from "../dbtypes";
import {
  CustomDashboard,
  dbAddr2ParaAddr,
  ICloudParameter,
  IDbAddress,
  IInput,
  InputType,
  IOutput,
  OutputMode,
  paraAddr2DbAddr,
} from "parameter";
import {
  ChargingMode as ApiChargingMode,
  ExecCommandMutation,
  ImportFileInput,
  ImportFileMutation,
  ListImportFilesQuery,
  ListImportFilesQueryVariables,
  ListRefusedRfidsQuery,
  ListRefusedRfidsQueryVariables,
  ListRfidsBasicQuery,
  ListUsersBasicQuery,
  ListUsersQueryVariables,
  ModifyChargerControlMutation,
  ModifyChargerMutation,
  ModifyConnectorInput,
  ModifyConnectorMutation,
  ModifyRfidMutation,
  ModifySiteMutation,
  ModifyTariffMutation,
  ModifyUserMutation,
  TriggerChargerCommandMutation,
} from "../graphql/api";

import {
  listSites as listSitesGql,
  listUsers as listUsersGql,
  getParameter as getParameterGql,
  getDataBoundaries as getDataBoundariesGql,
  listTrendData as listTrendDataGql,
  listIoData as listIoDataGql,
  listAlarms as listAlarmsGql,
  listChargers,
  listConnectors,
  listRfids,
  listChargingSessions,
  triggerReport as triggerReportGql,
  listChargerStatusNotifications,
  checkRfid,
  listTariffs,
  getChargerControl,
  listRefusedRfids,
  listImportFiles,
} from "graphql/queries";
import {
  modifyUser as modifyUserGql,
  modifySite as modifySiteGql,
  modifyCharger as modifyChargerGql,
  modifyConnector as modifyConnectorGql,
  modifyRfid as modifyRfidGql,
  modifyTariff as modifyTariffGql,
  newTrendData as newTrendDataGql,
  newAlarmData as newAlarmDataGql,
  execCommand as execCommandGql,
  triggerChargerCommand as triggerChargerCommandGql,
  modifyChargerControl as modifyChargerControlGql,
  importFile as importFileGql,
} from "graphql/mutations";
import {
  GetParameterQuery,
  GetParameterQueryVariables,
  GetDataBoundariesQuery,
  GetDataBoundariesQueryVariables,
  ListSitesQuery,
  ListUsersQuery,
  ListTrendDataQuery,
  ListTrendDataQueryVariables,
  ListIoDataQuery,
  ListIoDataQueryVariables,
  ListAlarmsQuery,
  ListAlarmsQueryVariables,
  ListChargersQuery,
  ListChargersQueryVariables,
  ListConnectorsQuery,
  ListConnectorsQueryVariables,
  ListRfidsQuery,
  ListRfidsQueryVariables,
  ListChargingSessionsQueryVariables,
  ListChargingSessionsQuery,
  ModifyUserInput,
  ModifySiteInput,
  ModifyChargerInput,
  ModifyRfidInput,
  NewTrendDataInput,
  NewTrendDataMutation,
  NewAlarmDataInput,
  NewAlarmDataMutation,
  TriggerReportQueryVariables,
  Aggregation,
  UserRole,
  ExecCommandInput,
  Command,
  ReportFormat,
  DataSource,
  ListChargerStatusNotificationsQueryVariables,
  ListChargerStatusNotificationsQuery,
  GetChargerControlQueryVariables,
  GetChargerControlQuery,
  CheckRfidQueryVariables,
  CheckRfidQuery,
  ModifyTariffInput,
  ListTariffsQueryVariables,
  ListTariffsQuery,
  TriggerChargerCommandMutationVariables,
  ModifyChargerControlMutationVariables,
  ChargerCommand,
} from "graphql/api";
import amplifyconfig from "amplifyconfiguration.json";

import {
  ActionType,
  raiseAction,
  IAction,
  IActionTypeLogin,
  IActionTypeLoginResult,
  IActionCloudTrendDataRow,
  IActionChangeInput,
  IActionChangeOutput,
  IActionTypeChangeAwsUser,
  IActionTypeUserlist,
  IActionTypeSitelist,
  IActionTypeChangeUser,
  IActionAckAlarm,
  IActionChangeTrend,
  IActionTypeChangeSite,
  IActionTypeParameterLoad,
  IActionTypeChargingSessionList,
  IActionTypeChangeReports,
  IActionTypeChangeReportsSettings,
  IActionChangeChargerSettings,
  IActionTypeRfidList,
  IActionChangeRfid,
  IActionCloudTrendInfo,
  IActionCloudIoDataRow,
  IActionTypeChangeTrendMonitorSettings,
  IActionOcppTrendInfo,
  IActionOcppCharger,
  IActionTypeChargerStateChangeList,
  IActionChangeTariff,
  IActionTypeForgotPassword,
  IActionOcppConnector,
  IActionNewChargingSessionData,
  IActionChargerCommand,
  IActionChargingPower,
  IActionCloudIoTrendData,
  IActionCommand,
  IActionCreateReport,
  IActionTypeSignUp,
  IActionTypeSignUpResult,
  IActionCloudTrendData,
  IActionImporterFileUpload,
} from "actions";
import { IParameter, IVirtAssign, IVirtSiteAssign } from "parameter";
import { TimePeriod, ICloudTrendMember } from "types";
import { DeviceType, ITrendReportFilter } from "dbtypes";
import { isSet } from "utils";
import {
  parseApiBool,
  parseApiFloat,
  parseApiInt,
  parseApiItemArray,
  parseApiString,
} from "apiutils";
import {
  getGraphBoundaryDates,
  getCustomPeriodAggregation,
  isOcppFrontend,
  makeCommandSeqNr,
  parseVirtualSiteId,
} from "guiutils";
import { IState } from "states/states";
import { Site, User } from "states/sitestates";
import { IOcppChargingSession, IRefusedRfid, IRfid, ITariff } from "states/ocppstates";
import { IChargerStateEvent, ITrendDataRow } from "states/trendingstates";
import { IAlarmEntry, AlarmSource } from "comtypes";
import { ApiSagas, getAlarmState, makeXaid } from "./apisagas";
import { ConnectorStatus, StopReason } from "model/connector";
import { CloudLiveDataHandler, IRegisterPushSelection } from "./cloudlivedatahandler";
import { createBrowserRouter } from "react-router-dom";
import { listRfidsBasic, listUsersBasic } from "graphql/customqueries";

// the maximum query limit depends on the amount of tariff circuits and the time period
const TREND_LOADING_LIMIT = 10; // tariff circuit years
// live data timeout on server is 5 minutes, so we time out here shortly before this
const LIVE_DATA_REGISTRATION_TIMEOUT = 290 * 1000; // ms

// overwrite configuration
const EMBYT_GRAPHQL_ENDPOINT = "https://enl-gql.embyt.com/graphql";

export function cloudTrend2LinxTrend(
  curData: ICloudTrendMember & {
    timestamp: number;
    siteId: number;
    chargerId?: number | null | undefined;
    tlId: number;
  },
) {
  const data: ITrendDataRow = {
    regperiod: curData.regperiod,
    energy: curData.energy,
    power: curData.power,
    costs: curData.costs ?? undefined,
    tariff: curData.tariff ?? undefined,
    targetpower: curData.targetpower ?? undefined,
    soc: curData.soc ?? undefined,
  };
  const row: IActionCloudTrendDataRow = {
    timestamp: curData.timestamp,
    siteId: curData.siteId,
    chargerId: curData.chargerId ?? undefined,
    tlId: curData.tlId,
    data,
  };
  return row;
}

export class CloudSagas extends ApiSagas {
  private RFID_USER_POOL = "eu-central-1_XUY9ZZapR";
  private RFID_APP_CLIENT = "3rq5upkud0fprjeoir101inftn";

  private API = generateClient();

  private liveDataHandler: CloudLiveDataHandler | undefined;

  // main entry point to load sagas at startup
  public *initSagas() {
    yield* super.initSagas();

    yield takeEvery(ActionType[ActionType.ACT_VISIBILITY_CHANGE], this.visibilityChange.bind(this));

    yield takeLatest(ActionType[ActionType.ACT_SIGNUP_REQ], this.doSignUp.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_SIGNUP_RESEND_REQ], this.doSignUpResend.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_SIGNUP_CONFIRM_REQ],
      this.doSignUpConfirm.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_FORGOT_PASSWORD_CODE_REQ],
      this.reqPasswordCode.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ],
      this.reqPasswordChange.bind(this),
    );
    yield takeEvery(ActionType[ActionType.ACT_CHANGE_AWSUSER_REQ], this.changeAwsUser.bind(this));
    yield takeEvery(ActionType[ActionType.ACT_CHANGE_USER_REQ], this.changeUser.bind(this));
    yield takeEvery(
      ActionType[ActionType.ACT_LOGIN_CREATE_RFID_USER],
      this.createRfidUser.bind(this),
    );

    yield takeEvery(
      ActionType[ActionType.ACT_CHANGE_SITESETTINGS_REQ],
      this.changeSiteSettings.bind(this),
    );
    yield takeEvery(ActionType[ActionType.ACT_SAVE_REPORT_REQ], this.changeReports.bind(this));
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_REPORTS_SETTINGS_REQ],
      this.changeReportsSettings.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_REQ],
      this.changeTrendMonitorSettings.bind(this),
    );
    yield takeLatest(ActionType[ActionType.ACT_CREATE_REPORT_REQ], this.createReport.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_SITES_REQ], this.getSites.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_USERLIST_BASIC_REQ],
      this.getUserlistBasic.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_USERLIST_EXT_REQ],
      this.getUserlistExt.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ],
      (action: IAction<boolean>) => this.registerForPushData(action.payload),
    );
    yield takeLatest([ActionType[ActionType.ACT_DISCONNECT_SITE]], this.unsubscribeData.bind(this));
    yield takeEvery(ActionType[ActionType.ACT_CHANGE_TREND_REQ], this.changeTrendData.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_CHARGERS_REQ],
      this.getOcppChargers.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_OCPP_CHARGERS_REQ],
      this.saveOcppCharger.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ],
      this.getOcppRfidsBasic.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ],
      this.getOcppRfidsExt.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_REFUSED_RFIDS_REQ],
      this.getOcppRefusedRfids.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_CHECK_OCPP_RFIDS_REQ],
      this.checkOcppRfid.bind(this),
    );
    yield takeEvery(ActionType[ActionType.ACT_SAVE_OCPP_RFID_REQ], this.saveOcppRfid.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ],
      this.getOcppTariffs.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_OCPP_TARIFF_REQ],
      this.saveOcppTariff.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_CONNECTORS_REQ],
      this.getOcppConnectors.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_PENDING_OCPP_SESSIONS_REQ],
      this.getPendingOcppSessions.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_SESSIONS_REQ],
      this.getOcppSessions.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_TRENDINFO_REQ],
      this.getOcppTrendInfo.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_SELECT_CHARGING_SESSION],
      this.getChargingSession.bind(this),
    );

    yield takeLatest(
      ActionType[ActionType.ACT_NEW_CHARGING_SESSION_DATA],
      this.newChargingSession.bind(this),
    );

    yield takeEvery(
      ActionType[ActionType.ACT_TRIGGER_CHARGER_CMD_REQ],
      this.triggerChargerCommand.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_CHARGER_POWER_REQ],
      this.saveChargerPower.bind(this),
    );

    yield takeLatest(ActionType[ActionType.ACT_OPEN_SSH_TUNNEL_REQ], this.openSshTunnel.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_REBOOT_REQ], this.doReboot.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_PERFORM_FILE_UPLOAD_REQ],
      this.performFileUpload.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_FILEUPLOADS_REQ],
      this.getFileUploads.bind(this),
    );
  }

  public async initApi(store: Store) {
    // overwrite the graphql endpoint URL
    amplifyconfig.aws_appsync_graphqlEndpoint = EMBYT_GRAPHQL_ENDPOINT;

    // setup communication interface to the cloud server
    // do we authenticate with RFID card?
    // routerState may not be available at this point, so we use the URL path
    if (window.location.pathname.startsWith(`/${RootPath.RFID}`)) {
      console.log("using RFID user pool");
      amplifyconfig.aws_user_pools_id = this.RFID_USER_POOL;
      amplifyconfig.aws_user_pools_web_client_id = this.RFID_APP_CLIENT;
    }

    // configure the graphql endpoint and default authentication
    Amplify.configure(amplifyconfig);

    // setup live data handler
    this.liveDataHandler = new CloudLiveDataHandler(this.API, store);
  }

  public *doAutoLogin() {
    // check for auto-login as URL argument
    let autoLogin: IActionTypeLogin | undefined;
    if (new URL(window.document.URL).searchParams.has("login")) {
      const auth = new URL(window.document.URL).searchParams.get("login")!;
      autoLogin = JSON.parse(Base64.decode(auth)) as IActionTypeLogin;
    }
    // routerState may not be available at this point, so we use the URL path
    const isRfidUser = window.location.pathname.startsWith(`/${RootPath.RFID}`);
    // check AWS cached login
    let userData: IActionTypeLoginResult | undefined;
    try {
      const curUser = (yield getCurrentUser()) as GetCurrentUserOutput;
      if (isRfidUser) {
        userData = {
          // we use the rfid as userId, not the AWS userId
          userId: curUser.username,
          isRfidUser,
        };
      } else {
        const curAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
        userData = {
          userId: curUser.userId,
          email: curAttributes?.email,
          realName: curAttributes?.name,
          isRfidUser,
        };
      }
    } catch (error) {
      // getCurrentUser throws if not logged in
      // it also throws with "PasswordResetRequiredException", which is fine
      if ((error as Error).name !== "UserUnAuthenticatedException") {
        console.log("auto-login failed", error);
      }
    }

    // AWS cached login has preference to URL login, when user matches
    if (userData && (!autoLogin || userData.email === autoLogin.username)) {
      console.log("resuming login session");
      yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
    }
    // alternatively, we may auto-login using URL
    else if (autoLogin) {
      console.log("perform auto-login");
      yield put(raiseAction(ActionType.ACT_LOGIN_REQ, autoLogin));
    }
  }

  protected *doSignUp(action: IAction<IActionTypeSignUp>) {
    try {
      // AWS user creation workflow
      const result = (yield signUp({
        username: action.payload.username,
        password: action.payload.password ?? "",
        options: {
          userAttributes: {
            name: action.payload.realName,
            email: action.payload.username,
            // we need to send an empty string if the user signs up by himself
            // as this is only allowed for prepared users (company already set) or pre-defined domains
            "custom:company": "",
          },
        },
      })) as SignUpOutput | undefined;

      if (result?.isSignUpComplete && result.nextStep.signUpStep === "DONE") {
        const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
        const userData: IActionTypeSignUpResult = {
          userId: result.userId,
          realName: userAttributes.name,
          destination: userAttributes.email,
          confirmed: true,
        };
        yield put(raiseAction(ActionType.ACT_SIGNUP_SUCC, userData));

        // try to login now!
        const loginData: IActionTypeLogin = {
          username: action.payload.username,
          password: action.payload.password ?? "",
        };
        yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
      } else if (result?.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        // set flag to invoke page to show that the sign-up confirmation is still pending!
        yield put(
          raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CONFIRMATION_PENDING),
        );
        yield put(
          raiseAction(ActionType.ACT_SIGNUP_CONFIRM, {
            username: action.payload.username,
            keepErrorState: true,
          } as IActionTypeSignUp),
        );
      } else if (result?.userId) {
        console.error("unexpected signup step", result);
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL, result.nextStep.signUpStep));
      } else {
        console.error("unexpected auth response", result);
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL, result));
      }
    } catch (error) {
      // "EmptySignUpUsername"
      // "UsernameExistsException"
      // "InvalidLambdaResponseException"
      if ((error as Error).name === "UsernameExistsException") {
        // yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSErrorFeedback.SIGNUP_USER_EXISTS));

        // try to login
        const loginData: IActionTypeLogin = {
          username: action.payload.username,
          password: action.payload.password ?? "",
        };
        yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
      } else if ((error as Error).name === "EmptySignUpUsername") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.USERNAME_MISSING));
      } else if (
        (error as Error).name === "UserLambdaValidationException" &&
        (error as Error).message.includes("unsupported company")
      ) {
        yield put(
          raiseAction(
            ActionType.ACT_COGNITO_FAIL_AWS,
            UserStateAWSError.SIGNUP_UNSUPPORTED_COMPANY,
          ),
        );
      } else {
        yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL, error);
      }
    }
  }

  protected *doSignUpResend(action: IAction<IActionTypeSignUp>) {
    const username = action.payload.username;
    try {
      // AWS loging workflow
      const result = (yield resendSignUpCode({
        username,
      })) as ResendSignUpCodeOutput | undefined;
      const userData: IActionTypeSignUpResult = {
        destination: result?.destination ?? "",
        attribute: result?.attributeName ?? "email",
        medium: result?.deliveryMedium ?? "EMAIL",
        confirmed: false,
      };
      yield put(raiseAction(ActionType.ACT_SIGNUP_SUCC, userData));
    } catch (error) {
      // catch error when an RFID user newly gets created
      // not sure if there is a better approach as string compare
      if ((error as Error).name === "InvalidParameterException") {
        console.log("User already confirmed. Try to login...");
        yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL, error as Error);
      } else if ((error as Error).name === "EmptySignUpUsername") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.USERNAME_MISSING));
      } else if ((error as Error).name === "UserNotFoundException") {
        console.log("User not found...");
        yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL, error as Error);
      } else {
        yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL, error as Error);
      }
    }
  }

  protected *doSignUpConfirm(action: IAction<IActionTypeSignUp>) {
    const username = action.payload.username;
    try {
      // AWS loging workflow
      const result = (yield confirmSignUp({
        username,
        confirmationCode: action.payload.code ?? "",
      })) as ResendSignUpCodeOutput | undefined;
      const userData: IActionTypeSignUpResult = {
        destination: result?.destination ?? "",
        attribute: result?.attributeName ?? "email",
        medium: result?.deliveryMedium ?? "EMAIL",
        confirmed: true,
      };
      yield put(raiseAction(ActionType.ACT_SIGNUP_SUCC, userData));

      // try to login now!
      const loginData: IActionTypeLogin = {
        username: action.payload.username,
        password: action.payload.password ?? "",
      };
      yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
    } catch (error) {
      if ((error as Error).name === "EmptyConfirmSignUpUsername") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.USERNAME_MISSING));
      } else if ((error as Error).name === "CodeMismatchException") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CODE_MISMATCH));
      } else if ((error as Error).name === "ExpiredCodeException") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CODE_EXPIRED));
      } else if ((error as Error).name === "EmptyConfirmSignUpCode") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CODE_MISSING));
      } else if ((error as Error).name === "InvalidPasswordException") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.PASSWORD_INVALID));
      } else if ((error as Error).name === "NotAuthorizedException") {
        yield put(
          raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.ALREADY_CONFIRMED),
        );
      } else {
        yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL, error);
      }
    }
  }

  protected *doLogin(action: IAction<IActionTypeLogin>) {
    const state = (yield select(this.getState)) as IState;
    const isRfidUser = state.router.rootPath === RootPath.RFID;
    const username = isRfidUser ? action.payload.username.toLowerCase() : action.payload.username;
    try {
      // login workflow of rfid user does not have much in common to the AWS login
      if (isRfidUser) {
        const result = (yield signIn({
          username,
          options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
        })) as SignInOutput | undefined;
        if (result?.isSignedIn && result.nextStep.signInStep === "DONE") {
          const userData: IActionTypeLoginResult = {
            // we use the rfid as userId, not the AWS userId
            userId: username,
            isRfidUser,
          };
          yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
        } else {
          console.error("unexpected rfid auth response", result);
          yield put(raiseAction(ActionType.ACT_LOGIN_FAIL, result));
        }
      }
      // AWS loging workflow
      else {
        const result = (yield signIn({
          username,
          password: action.payload.password,
        })) as SignInOutput | undefined;
        if (result?.isSignedIn && result.nextStep.signInStep === "DONE") {
          const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
          const userData: IActionTypeLoginResult = {
            userId: userAttributes.sub!,
            isRfidUser,
            realName: userAttributes.name,
            email: userAttributes.email,
          };
          yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
        } else if (result?.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          // set flag to invoke password change form
          yield put(raiseAction(ActionType.ACT_FORCE_PWD_CHANGE, username));
        } else if (result?.nextStep.signInStep === "CONFIRM_SIGN_UP") {
          // set flag to invoke page to show that the sign-up confirmation is still pending!
          // yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CONFIRMATION_PENDING));
          // link to Code-Entry-Screen
          const payload: IActionTypeSignUp = {
            username,
            password: action.payload.password,
            keepErrorState: true,
          };
          yield put(raiseAction(ActionType.ACT_SIGNUP_CONFIRM, payload));
        } else if (result?.isSignedIn) {
          console.error("unexpected login step", result);
          yield put(raiseAction(ActionType.ACT_LOGIN_FAIL, result.nextStep.signInStep));
        } else {
          console.error("unexpected auth response", result);
          yield put(raiseAction(ActionType.ACT_LOGIN_FAIL, result));
        }
      }
    } catch (error) {
      // catch error when an RFID user newly gets created
      // not sure if there is a better approach as string compare
      // should we do more detailed evaluation??
      // yield put(raiseAction(ActionType.ACT_LOGIN_FAIL, UserStateAWSError.PASSWORD_CHANGE_FAIL));
      //
      // for now -> just forward the exception text

      if (["UserNotFoundException", "NotAuthorizedException"].includes((error as Error).name)) {
        if (isRfidUser) {
          yield put(raiseAction(ActionType.ACT_LOGIN_CREATE_RFID_USER, username));
        } else {
          yield put(raiseAction(ActionType.ACT_LOGIN_FAIL_AWS, UserStateAWSError.LOGIN_FAILED));
        }
      } else if ((error as Error).name === "EmptySignInUsername") {
        yield put(raiseAction(ActionType.ACT_LOGIN_FAIL_AWS, UserStateAWSError.USERNAME_MISSING));
      } else if ((error as Error).name === "EmptySignInPassword") {
        yield put(raiseAction(ActionType.ACT_LOGIN_FAIL_AWS, UserStateAWSError.PASSWORD_MISSING));
      } else if ((error as Error).name === "UserAlreadyAuthenticatedException") {
        console.log("already logged in, but not valid. logging out and retrying.");
        yield signOut();
        // retry login
        yield put(raiseAction(ActionType.ACT_LOGIN_REQ, action.payload));
      } else {
        // all other errors, send error-text only!
        yield this.raiseHttpError(ActionType.ACT_LOGIN_FAIL, error);
      }
    }
  }

  protected *doLogout(action: IAction<undefined>) {
    // site connection active?
    const state = (yield select(this.getState)) as IState;
    if (isSet(state.cached.selectedSite)) {
      // we need to disconnect before logout to avoid errors at unsubscribing live data
      yield put(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    }
    try {
      // perform logout
      yield signOut();
      yield put(raiseAction(ActionType.ACT_LOGOUT_SUCC));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_LOGOUT_FAIL, error);
    }
  }

  protected *reqPasswordCode(action: IAction<IActionTypeForgotPassword>) {
    const payload = action.payload;
    try {
      const result = (yield resetPassword({ username: payload.username })) as ResetPasswordOutput;
      yield put(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CODE_SUCC, result));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL, error);
    }
  }

  protected *reqPasswordChange(action: IAction<IActionTypeForgotPassword>) {
    const payload = action.payload;
    try {
      const result = (yield confirmResetPassword({
        username: payload.username,
        newPassword: payload.newPassword,
        confirmationCode: payload.confirmationCode,
      })) as string;
      yield put(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CHANGE_SUCC, result));
    } catch (error) {
      // exceptions:
      //  InvalidPasswordException (if length or characters are not ok)
      if ((error as Error).name === "EmptyConfirmResetPasswordConfirmationCode") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CODE_MISSING));
      } else if ((error as Error).name === "CodeMismatchException") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.CODE_MISMATCH));
      } else if ((error as Error).name === "InvalidPasswordException") {
        yield put(raiseAction(ActionType.ACT_COGNITO_FAIL_AWS, UserStateAWSError.PASSWORD_INVALID));
      } else {
        console.error("unexpected pwd change error", (error as Error).name);
        yield this.raiseHttpError(ActionType.ACT_COGNITO_FAIL_AWS, error);
      }
    }
  }

  protected *changeAwsUser(action: IAction<IActionTypeChangeAwsUser>) {
    // this can only happen in cloud usage
    const isPasswordChange = action.payload.realName === undefined;
    try {
      if (isPasswordChange) {
        yield updatePassword({
          oldPassword: action.payload.oldPassword,
          newPassword: action.payload.newPassword,
        });
        yield put(raiseAction(ActionType.ACT_CHANGE_AWSUSER_SUCC));
      } else {
        // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
        const result = (yield confirmSignIn({
          challengeResponse: action.payload.newPassword,
          options: {
            userAttributes: {
              name: action.payload.realName,
            },
          },
        })) as ConfirmSignInOutput;
        if (result.isSignedIn && result.nextStep.signInStep === "DONE") {
          // finish login sequence
          const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
          const userData: IActionTypeLoginResult = {
            userId: userAttributes.sub!,
            isRfidUser: false,
            realName: userAttributes.name,
            email: userAttributes.email,
          };
          yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
        } else {
          console.error("unexpected confirmSignin result", result);
          yield put(raiseAction(ActionType.ACT_CHANGE_AWSUSER_FAIL, result));
        }
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_AWSUSER_FAIL, error);
    }
  }

  protected *changeUser(action: IAction<IActionTypeChangeUser>) {
    const args: ModifyUserInput = {
      id: isSet(action.payload.id) ? `${action.payload.id}` : undefined,
      email: action.payload.email?.trim(),
      company: action.payload.company?.trim(),
      name: action.payload.name?.trim(),
      role: action.payload.role,
      siteIds: action.payload.userSites,
      accessRights: isSet(action.payload.accessRights)
        ? JSON.stringify(action.payload.accessRights)
        : undefined,
      settings: isSet(action.payload.settings)
        ? JSON.stringify(action.payload.settings)
        : undefined,
      chargingControl: isSet(action.payload.chargerControl)
        ? JSON.stringify(action.payload.chargerControl)
        : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifyUserGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyUserMutation> | undefined;

      // result may be undefined if this.API is undefined, which must not happen
      if (result?.data.modifyUser) {
        const shouldEditUser = !isSet(action.payload.id);
        // the result holds the user-id which was modifed or created!
        action.payload.id = parseInt(result.data.modifyUser);
        yield put(raiseAction(ActionType.ACT_CHANGE_USER_SUCC, action.payload));
        // if payload.id is undefined -> a new user was created!
        // navigate to user, but in editmode! (can we get the id somehow via graphQL response??!!)
        if (shouldEditUser) {
          // and switch to this user for editing!
          const router: ReturnType<typeof createBrowserRouter> = yield getContext("router");
          router.navigate(`../${MainPanel.USER_SETTINGS}?user=${result.data.modifyUser}`);
        }
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_USER_FAIL));
      }
    } catch (err: any) {
      const errors = err["errors"];
      if (errors?.[0]?.message.includes("duplicate key value violates unique constraint")) {
        yield put(raiseAction(ActionType.ACT_CHANGE_USER_FAIL, "User exists already!"));
      } else {
        yield this.raiseHttpError(ActionType.ACT_CHANGE_USER_FAIL, err);
      }
    }

    // check for modification of rfid card properties, which is only possible for rfid users here
    const state = (yield select(this.getState)) as IState;
    const isRfidUser = !!state.site.user?.isRfidUser;
    if (isRfidUser && (action.payload.carName || action.payload.carBattery)) {
      const args: ModifyRfidInput = {
        // the rfid selection will be based on user ID
        carName: action.payload.carName,
        carBattery: action.payload.carBattery,
      };
      try {
        const result = (yield this.API?.graphql({
          query: modifyRfidGql,
          variables: { data: args },
        })) as GraphQLResult<ModifyRfidMutation> | undefined;
        if (result?.data.modifyRfid) {
          // reload changed data
          yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ));
        } else {
          yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_RFID_FAIL, "RFID modification failed");
        }
      } catch (error) {
        yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_RFID_FAIL, error);
      }
    }
  }

  private createRfidEmail(rfid: string) {
    return `rfid-${rfid}@embyt.com`;
  }

  protected *createRfidUser(action: IAction<string>) {
    const rfid = action.payload;
    const password = "<no-rfid-password>"; // not needed for rfid users
    const email = this.createRfidEmail(rfid);
    try {
      const result = (yield signUp({
        username: rfid,
        password,
        options: {
          userAttributes: {
            name: rfid,
            email,
          },
        },
      })) as SignUpOutput;
      console.log("new user signup", rfid, result);
      // after user creation, trigger login
      const loginData: IActionTypeLogin = {
        username: rfid,
        password,
      };
      yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_LOGIN_FAIL, error);
    }
  }

  protected *changeSiteSettings(action: IAction<IActionTypeChangeSite>) {
    const state = (yield select(this.getState)) as IState;
    // take care: we must only use the direct (alarm) settings from the action payload
    // the report and trend monitor settings must be taken from the state space
    // as the might have been modified in the meanwhile
    const cloudSettings = action.payload.cloudSettings ?? {};
    const stateCloudSettings = state.site.siteList?.find(
      (curItem) => curItem.id === action.payload.id,
    )?.cloudSettings;
    cloudSettings.reports = stateCloudSettings?.reports;
    cloudSettings.trendMonitor = stateCloudSettings?.trendMonitor;
    const args: ModifySiteInput = {
      id: `${action.payload.id}`,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(raiseAction(ActionType.ACT_CHANGE_SITESETTINGS_SUCC, action.payload));
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_SITESETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_SITESETTINGS_FAIL, error);
    }
  }

  protected *changeReports(action: IAction<IActionTypeChangeReports>) {
    const state = (yield select(this.getState)) as IState;
    const sites = state.site.siteList?.filter((curItem) => curItem.id === action.payload.siteId);
    if (sites?.length !== 1) {
      yield put(raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL, "site settings not found"));
      return;
    }
    const cloudSettings = { ...sites[0].cloudSettings };
    if (!Array.isArray(cloudSettings.reports)) {
      cloudSettings.reports = [];
    } else {
      // clone reports array to avoid modifying source data structure
      cloudSettings.reports = [...cloudSettings.reports];
    }
    // new report, deleted or modified item?
    if (action.payload.reportId === undefined) {
      // new report
      cloudSettings.reports.push(action.payload.report!);
    } else if (action.payload.report === undefined) {
      // deleted report, and reindex remaining items
      cloudSettings.reports.splice(action.payload.reportId, 1);
    } else {
      // modified report
      cloudSettings.reports[action.payload.reportId] = action.payload.report;
    }
    const args: ModifySiteInput = {
      id: `${action.payload.siteId}`,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(
          raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC, {
            id: action.payload.siteId,
            cloudSettings,
          }),
        );
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL, error);
    }
  }

  protected *changeReportsSettings(action: IAction<IActionTypeChangeReportsSettings>) {
    const state = (yield select(this.getState)) as IState;
    const sites = state.site.siteList?.filter((curItem) => curItem.id === action.payload.siteId);
    if (sites?.length !== 1) {
      yield put(raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL, "site settings not found"));
      return;
    }
    const cloudSettings = { ...sites[0].cloudSettings };
    // modified reports-Settings
    cloudSettings.reportsSettings = action.payload.reportsSettings;

    const args: ModifySiteInput = {
      id: `${action.payload.siteId}`,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(
          raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC, {
            id: action.payload.siteId,
            cloudSettings,
          }),
        );
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL, error);
    }
  }

  protected *changeTrendMonitorSettings(action: IAction<IActionTypeChangeTrendMonitorSettings>) {
    const state = (yield select(this.getState)) as IState;
    const sites = state.site.siteList?.filter((curItem) => curItem.id === action.payload.siteId);
    if (sites?.length !== 1) {
      yield put(
        raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL, "site settings not found"),
      );
      return;
    }
    const cloudSettings = { ...sites[0].cloudSettings };
    if (!Array.isArray(cloudSettings.trendMonitor)) {
      cloudSettings.trendMonitor = [];
    } else {
      // clone reports array to avoid modifying source data structure
      cloudSettings.trendMonitor = [...cloudSettings.trendMonitor];
    }
    // new trend monitor, deleted or modified item?
    if (action.payload.trendMonitorId === undefined) {
      // new item
      cloudSettings.trendMonitor.push(action.payload.trendMonitorSettings!);
    } else if (action.payload.trendMonitorSettings === undefined) {
      // deleted trend monitor, and reindex remaining items
      cloudSettings.trendMonitor.splice(action.payload.trendMonitorId, 1);
    } else {
      // modified item
      cloudSettings.trendMonitor[action.payload.trendMonitorId] =
        action.payload.trendMonitorSettings;
    }
    const args: ModifySiteInput = {
      id: `${action.payload.siteId}`,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(
          raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC, {
            id: action.payload.siteId,
            cloudSettings,
          }),
        );
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL, error);
    }
  }

  protected *createReport(action: IAction<IActionCreateReport>) {
    const reportId = action.payload.reportId;
    const state = (yield select(this.getState)) as IState;

    // construct customization filter
    const filter: ITrendReportFilter[] = [];
    if (state.gui.reportOptions.startDate) {
      // local state in ms; API in s
      filter.push({ type: "begin", ref: state.gui.reportOptions.startDate / 1000 });
    }
    if (state.gui.reportOptions.endDate) {
      filter.push({ type: "end", ref: state.gui.reportOptions.endDate / 1000 });
    }

    const args: TriggerReportQueryVariables = {
      siteId: state.cached.selectedSite!,
      reportId,
      outputFormat: action.payload.previewOnly ? ReportFormat.data : ReportFormat.link,
      customization:
        filter.length > 0
          ? JSON.stringify({
              filter,
            })
          : undefined,
    };
    try {
      yield this.API?.graphql({
        query: triggerReportGql,
        variables: args,
      }); //as GraphQLResult<TriggerReportMutation>;
      yield put(
        raiseAction(
          action.payload.previewOnly
            ? ActionType.ACT_CREATE_REPORT_DATA_SUCC
            : ActionType.ACT_CREATE_REPORT_SUCC,
          reportId,
        ),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CREATE_REPORT_FAIL, error);
    }
  }

  protected *getSites(action: IAction<undefined>) {
    try {
      const result = (yield this.API?.graphql({
        query: listSitesGql,
      })) as GraphQLResult<ListSitesQuery> | undefined;
      const siteList = (result?.data?.listSites ?? []).map((curRow) => {
        const site: Site = {
          id: parseApiInt(curRow!.id),
          name: parseApiString(curRow?.name),
          serial: parseApiString(curRow?.serial),
          company: parseApiString(curRow?.company),
          longitude: parseApiFloat(curRow?.longitude),
          latitude: parseApiFloat(curRow?.latitude),
          deviceType: (curRow?.deviceType ?? DeviceType.LIOB_586) as DeviceType,
          deviceSw: parseApiString(curRow?.deviceSw),
          tzOffset: isSet(curRow?.tzOffset) ? parseApiInt(curRow?.tzOffset) : undefined,
          ipAddresses: isSet(curRow?.ipAddresses) ? parseApiString(curRow.ipAddresses) : undefined,
          cloudSettings: JSON.parse(curRow?.cloudSettings ? curRow?.cloudSettings : "{}"),
        };
        return site;
      }) as IActionTypeSitelist;
      yield put(raiseAction(ActionType.ACT_GET_SITES_SUCC, siteList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_SITES_FAIL, error);
    }
  }

  protected *getUserlistBasic(action: IAction<undefined>) {
    try {
      const result = (yield this.API?.graphql({
        query: listUsersBasic,
      })) as GraphQLResult<ListUsersBasicQuery> | undefined;
      const userList = (result?.data?.listUsers ?? []).map((curRow) => {
        const user: User = {
          id: parseApiInt(curRow?.id),
          secId: parseApiString(curRow?.secondaryId),
          email: parseApiString(curRow?.email),
          role: curRow?.role ?? UserRole.viewer,
          name: parseApiString(curRow?.name),
          company: parseApiString(curRow?.company),
          accessRights: JSON.parse(curRow?.accessRights ? curRow.accessRights : "{}"),
        };
        return user;
      }) as IActionTypeUserlist;
      yield put(raiseAction(ActionType.ACT_GET_USERLIST_BASIC_SUCC, userList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_USERLIST_BASIC_FAIL, error);
    }
  }

  protected *getUserlistExt(action: IAction<number | undefined>) {
    const userIds = isSet(action.payload) ? [action.payload] : [];
    const args: ListUsersQueryVariables = { userIds };
    try {
      const result = (yield this.API?.graphql({
        query: listUsersGql,
        variables: args,
      })) as GraphQLResult<ListUsersQuery> | undefined;
      const userList = (result?.data?.listUsers ?? []).map((curRow) => {
        const user: User = {
          id: parseApiInt(curRow?.id),
          secId: parseApiString(curRow?.secondaryId),
          email: parseApiString(curRow?.email),
          role: curRow?.role ?? UserRole.viewer,
          name: parseApiString(curRow?.name),
          company: parseApiString(curRow?.company),
          accessRights: JSON.parse(curRow?.accessRights ? curRow.accessRights : "{}"),
          settings: JSON.parse(curRow?.settings ? curRow.settings : "{}"),
          siteIds: parseApiItemArray(curRow?.siteIds, parseApiInt),
          chargingControl: curRow?.chargingControl ? JSON.parse(curRow.chargingControl) : undefined,
        };
        return user;
      }) as IActionTypeUserlist;
      yield put(raiseAction(ActionType.ACT_GET_USERLIST_EXT_SUCC, userList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_USERLIST_EXT_FAIL, error);
    }
  }

  protected *getParameters(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: GetParameterQueryVariables = { siteId: state.cached.selectedSite! };
    try {
      let parameter: IParameter | undefined | null;
      const result = (yield this.API?.graphql({
        query: getParameterGql,
        variables: args,
      })) as GraphQLResult<GetParameterQuery> | undefined;
      // convert json string to object
      if (result?.data?.getParameter) {
        parameter = JSON.parse(result.data.getParameter) as IParameter | null;
      }
      yield put(
        raiseAction(ActionType.ACT_GET_PARAMETER_SUCC, {
          parameter: parameter ?? undefined,
        } as IActionTypeParameterLoad),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_PARAMETER_FAIL, error);
    }
  }

  protected *getAlarms(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListAlarmsQueryVariables = {
      siteId: state.cached.selectedSite!,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listAlarmsGql,
        variables: args,
      })) as GraphQLResult<ListAlarmsQuery> | undefined;
      // convert json string to object
      if (result?.data?.listAlarms) {
        const data: IAlarmEntry[] = result.data.listAlarms.map((curData) => {
          const row: IAlarmEntry = {
            xaid: makeXaid(curData!.source as AlarmSource, curData!.value),
            source: curData!.source as AlarmSource,
            alarmTime: curData!.alarmTimestamp * 1000,
            clearTime: curData!.clearTimestamp ? curData!.clearTimestamp * 1000 : null,
            value: curData!.value,
            state: getAlarmState(curData!.isActive, Boolean(curData!.ackTimestamp)),
            description: curData!.description,
          };
          return row;
        });
        yield put(raiseAction(ActionType.ACT_GET_ALARMS_SUCC, data));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_ALARMS_FAIL, error);
    }
  }

  protected *getTrendInfo(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: GetDataBoundariesQueryVariables = {
      siteId: state.cached.selectedSite!,
      dataSource: DataSource.Trend,
    };
    try {
      let result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      const minTrendTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxTrendTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined

      args.dataSource = DataSource.Input;
      result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      const minInputTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxInputTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined

      args.dataSource = DataSource.Output;
      result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      const minOutputTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxOutputTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined

      const payload: IActionCloudTrendInfo = {
        minTrendTimestamp,
        maxTrendTimestamp,
        minInputTimestamp,
        maxInputTimestamp,
        minOutputTimestamp,
        maxOutputTimestamp,
      };
      yield put(raiseAction(ActionType.ACT_GET_TRENDINFO_SUCC, payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_TRENDINFO_FAIL, error);
    }
  }

  protected *getOcppTrendInfo(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: GetDataBoundariesQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
      dataSource: DataSource.Charging,
    };
    try {
      const result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      // getDataBoundaries might be null if not trend data is present at all (e.g. for chargers)
      const minTrendTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxTrendTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined
      const payload: IActionOcppTrendInfo = {
        minTrendTimestamp,
        maxTrendTimestamp,
      };
      yield put(raiseAction(ActionType.ACT_GET_OCPP_TRENDINFO_SUCC, payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_TRENDINFO_FAIL, error);
    }
  }

  protected *getTrend(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
    const customDashboardSettings =
      (site?.cloudSettings?.customDashboard as CustomDashboard | undefined) ??
      state.parameter?.general?.customSettings?.customDashboard;

    // determine time range; we need special handling for custom dashboard view
    const isCustomDashboard =
      isSet(state.router.panel) &&
      [MainPanel.CUSTOM_DASHBOARD, MainPanel.PUBLIC_DISPLAY].includes(state.router.panel);
    const zoom = isCustomDashboard ? TimePeriod.CUSTOM : state.gui.trendPeriod.timePeriod;
    const now = Math.round(Date.now() / 1000);

    const startDate = isCustomDashboard
      ? now - (customDashboardSettings?.timerange ?? 7) * 24 * 3600
      : state.gui.trendPeriod.startDate;
    const endDate = isCustomDashboard ? now : state.gui.trendPeriod.endDate;
    const graphDates = getGraphBoundaryDates(startDate, endDate, state.site.tzOffset, zoom);
    const aggregation =
      isCustomDashboard && isSet(customDashboardSettings?.graph?.aggregation)
        ? (customDashboardSettings.graph.aggregation as Aggregation)
        : getCustomPeriodAggregation(zoom, graphDates.startDate, graphDates.endDate);
    // do we need to perform multiple requests due to high data count?
    const years =
      ((graphDates.endDate ?? Date.now() / 1000) - (graphDates.startDate ?? Date.now() / 1000)) /
      (3600 * 24 * 365);
    const chunkSize = Math.max(Math.round(TREND_LOADING_LIMIT / years), 1);
    const tlList =
      (state.parameter?.tariffline.length ?? 0) > chunkSize
        ? state.parameter?.tariffline.map((curItem, curIndex) => curItem.id ?? curIndex)
        : undefined;

    const args: ListTrendDataQueryVariables = {
      siteId: state.cached.selectedSite!,
      startDate: graphDates.startDate,
      endDate: graphDates.endDate,
      aggregation,
    };
    let pos = 0;
    try {
      do {
        args.tlId = tlList ? tlList.slice(pos, pos + chunkSize) : null;
        const result = (yield this.API?.graphql({
          query: listTrendDataGql,
          variables: args,
        })) as GraphQLResult<ListTrendDataQuery> | undefined;
        const data =
          result?.data?.listTrendData?.map((curData) => {
            return cloudTrend2LinxTrend(curData!);
          }) ?? [];
        pos += chunkSize;
        const progress = Math.min(pos / (tlList?.length ?? 1), 1);
        const payload: IActionCloudTrendData = {
          data,
          progress,
        };
        yield put(raiseAction(ActionType.ACT_GET_CLOUDTREND_SUCC, payload));
      } while (pos < (tlList?.length ?? 1));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_TREND_FAIL, error);
    }
  }

  private getIoParameterIndex(
    parameter: IInput[] | IOutput[],
    virtualSite: IVirtAssign[] | undefined,
    sourceSite: number | undefined,
    addr: IDbAddress,
    isOutput: boolean,
  ) {
    let index: number | undefined;
    // first, look in virtual sites as this is clearly identified
    const inAddr = dbAddr2ParaAddr(addr, isOutput);
    virtualSite?.forEach((curItem) => {
      if (curItem.sourceSite === sourceSite) {
        const parameterItem = parameter[curItem.destIndex!] as IInput | IOutput | undefined;
        if (
          parameterItem?.bus === inAddr.bus &&
          parameterItem?.module === inAddr.module &&
          parameterItem.channel === inAddr.channel
        ) {
          index = curItem.destIndex;
        }
      }
    });
    // did we find a matching virtual site?
    if (index === undefined) {
      // no virtual site found
      parameter.some((curItem, curIndex) => {
        if (
          curItem.bus === inAddr.bus &&
          curItem.module === inAddr.module &&
          curItem.channel === inAddr.channel
        ) {
          // ensure that this is no virtual item
          if (!virtualSite?.some((curAssign) => curAssign.destIndex === curIndex)) {
            index = curIndex;
            return true;
          }
        }
        return false;
      });
    }
    if (index === undefined) {
      console.error("cannot find io index", inAddr);
    }
    return index;
  }

  private cloudIoDataToActionData(
    graphqlData: ListIoDataQuery,
    parameter: ICloudParameter,
    isOutput: boolean,
  ) {
    const data: IActionCloudIoDataRow[] = [];
    const virtParameter = isOutput
      ? (parameter as ICloudParameter)?.virtualSite?.output
      : (parameter as ICloudParameter)?.virtualSite?.input;
    graphqlData.listIoData?.forEach((curData) => {
      const index = this.getIoParameterIndex(
        (isOutput ? parameter?.output : parameter?.input) ?? [],
        virtParameter,
        curData!.siteId,
        curData!,
        isOutput,
      );
      if (isSet(index)) {
        const row: IActionCloudIoDataRow = {
          siteId: curData!.siteId,
          timestamp: curData!.timestamp,
          source: isOutput ? "output" : "input",
          index,
          value: curData!.value,
          count: curData!.count,
        };
        data.push(row);
      }
    });
    return data;
  }

  protected *getIoTrend(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const zoom = state.gui.ioPeriod.timePeriod;
    const graphDates = getGraphBoundaryDates(
      state.gui.ioPeriod.startDate,
      state.gui.ioPeriod.endDate,
      state.site.tzOffset,
      zoom,
    );
    const aggregation = getCustomPeriodAggregation(zoom, graphDates.startDate, graphDates.endDate);
    // helper function
    const isDigital = (item: IInput | IOutput | undefined, isOutput: boolean) =>
      isSet(item) &&
      (isOutput
        ? (item as IOutput).mode !== OutputMode.ANALOG
        : (item as IInput).type !== InputType.ANALOG);

    const args: ListIoDataQueryVariables = {
      siteId: state.cached.selectedSite!,
      dataSource: DataSource.Input,
      startDate: graphDates.startDate,
      endDate: graphDates.endDate,
      aggregation,
      aggregationType:
        aggregation === Aggregation.NONE ? undefined : state.gui.selectedIoAggregationType,
    };

    const sources = [DataSource.Input, DataSource.Output];
    const ios: (IInput[] | IOutput[])[] = [
      state.parameter?.input.filter((input) => input.webActive === true) ?? [],
      state.parameter?.output.filter((output) => output.webActive === true) ?? [],
    ];
    let data: IActionCloudIoDataRow[] = [];
    let digitalIoCount = 0;
    try {
      for (let i = 0; i < sources.length; i++) {
        const isOutput = i === 1;
        args.dataSource = sources[i];
        args.ioList = ios[i].map((curItem) => ({
          ...paraAddr2DbAddr(curItem, isOutput),
          siteId: parseVirtualSiteId(curItem.id ?? 0).siteId,
        }));
        const result = (yield this.API?.graphql({
          query: listIoDataGql,
          variables: args,
        })) as GraphQLResult<ListIoDataQuery> | undefined;
        // convert json string to object
        if (!result?.data?.listIoData) {
          yield put(raiseAction(ActionType.ACT_GET_IOTREND_FAIL, "no input trend data received"));
          return;
        }
        const newData = this.cloudIoDataToActionData(
          result.data,
          state.parameter as ICloudParameter,
          isOutput,
        );
        digitalIoCount += data.reduce(
          (acc, curItem) =>
            acc +
            Number(
              isDigital(
                isOutput
                  ? state.parameter?.output[curItem.index]
                  : state.parameter?.input[curItem.index],
                isOutput,
              ),
            ),
          0,
        );
        data.push(...newData);
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_IOTREND_FAIL, error);
      return;
    }

    // indicator if more data is requested (AGGREGATION-COUNT DOES NOT EXCEED IO_TREND_LIMIT)
    const queryMoreData = aggregation !== Aggregation.NONE && digitalIoCount < IO_TREND_LIMIT;
    // notify action with received data
    const payload: IActionCloudIoTrendData = {
      aggregation,
      data,
      queryMoreData,
    };
    yield put(raiseAction(ActionType.ACT_GET_CLOUDIOTREND_SUCC, payload));

    // if we transmitted aggregated data, we also need to read full data for digital IO list view
    //  - but only if the count of all data-rows would not exceed our max-limit (IO_TREND_LIMIT)...
    if (queryMoreData) {
      args.aggregation = Aggregation.NONE;
      args.aggregationType = undefined;
      data = [];
      try {
        for (let i = 0; i < sources.length; i++) {
          const isOutput = i === 1;
          args.dataSource = sources[i];
          // only query digital ios
          args.ioList = ios[i]
            .filter((curItem) => isDigital(curItem, isOutput))
            .map((curItem) => paraAddr2DbAddr(curItem, isOutput));
          const result = (yield this.API?.graphql({
            query: listIoDataGql,
            variables: args,
          })) as GraphQLResult<ListIoDataQuery> | undefined;
          if (!result?.data?.listIoData) {
            yield put(raiseAction(ActionType.ACT_GET_CLOUDIOTREND_FULL_FAIL, "no IO data"));
            return;
          }
          const newData = this.cloudIoDataToActionData(
            result.data,
            state.parameter as ICloudParameter,
            isOutput,
          );
          data.push(...newData);
        }
        const fullPayload: IActionCloudIoTrendData = {
          aggregation: args.aggregation,
          data,
          queryMoreData: false,
        };
        yield put(raiseAction(ActionType.ACT_GET_CLOUDIOTREND_FULL_SUCC, fullPayload));
      } catch (error) {
        yield this.raiseHttpError(ActionType.ACT_GET_CLOUDIOTREND_FULL_FAIL, error);
        return;
      }
    } else {
      if (aggregation !== Aggregation.NONE)
        console.log("skip full IO request due to big amount of data", data.length, digitalIoCount);
    }
  }

  protected getUsedSites(
    selectedSite: number | undefined,
    virtualSite: IVirtSiteAssign | undefined,
  ) {
    const siteIds: number[] = [];
    // add main site
    if (isSet(selectedSite)) {
      siteIds.push(selectedSite);
    }
    // create list of sites we are using
    Object.values(virtualSite ?? {}).forEach((curGroup) => {
      (curGroup as IVirtAssign[]).forEach((curItem) => {
        if (!siteIds.includes(curItem.sourceSite)) {
          siteIds.push(curItem.sourceSite);
        }
      });
    });
    return siteIds;
  }

  private *visibilityChange(action: IAction<boolean>) {
    const state = (yield select(this.getState)) as IState;
    const isVisible = action.payload;
    if (isVisible) {
      // typically, all subscriptions are already in place and not touched
      // however, check for timeout of last live data registration
      const lastRequest = state.scheduler.allData.lastRequest ?? 0;
      if (Date.now() - lastRequest > LIVE_DATA_REGISTRATION_TIMEOUT) {
        yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT));
      }
      // renew live data registration is needed and performed
      yield this.registerForPushData(true);
    } else {
      // stop live data transmission
      if (isSet(state.cached.selectedSite)) {
        yield this.liveDataHandler?.unregisterPushData(state.cached.selectedSite);
      }
    }
  }

  protected *registerForPushData(forceUpdate: boolean) {
    const state = (yield select(this.getState)) as IState;
    // determine needed subscriptions
    const hasChargers = state.ocpp.charger.length > 0;
    const isOcppRestricted = isOcppFrontend(state.router.rootPath);
    const isRestricted = isOcppRestricted || state.router.rootPath === RootPath.TREND;
    const isRfidUser = !!state.site.user?.isRfidUser;
    const secUserId = state.site.user?.secId;
    const curUser = state.site.userList?.find(
      (curItem) => curItem.secId === state.site.user?.secId,
    );

    // we might not need subscriptions at all, e.g. when triggered via visibilityChange
    // but we did not select a site yet
    if ((isRfidUser && !isSet(secUserId)) || (!isRfidUser && !isSet(state.cached.selectedSite))) {
      console.log("skipping live data registration", isRfidUser, state.cached.selectedSite);
      return;
    }

    // when not restricted: we do not know whether we need live data yet
    // as we probably did not receive the parameters yet
    // so we assume to need them and avoid: && isSet(state.parameter)
    const selection: IRegisterPushSelection = {
      needsLiveData: !isRestricted,
      needsReportData: !isRfidUser,
      needsAlarmData: !isRestricted && !isRfidUser,
      needsChargerData: isOcppRestricted || hasChargers,
      needsControllerChargerData:
        !isRestricted && !isRfidUser && Boolean(curUser?.accessRights.showEnlineChargers),
    };
    yield this.liveDataHandler?.setupForPushData(
      state.cached.clientToken,
      isRfidUser ? undefined : state.cached.selectedSite,
      isRfidUser ? secUserId : undefined,
      state.ocpp.pendingChargingSession,
      selection,
      forceUpdate,
    );
  }

  protected *unsubscribeData(action?: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    if (isSet(state.cached.selectedSite)) {
      yield this.liveDataHandler?.unregisterPushData(state.cached.selectedSite);
    }
    yield this.liveDataHandler?.unsubscribePushData();
  }

  protected *setInput(action: IAction<IActionChangeInput>) {
    const state = (yield select(this.getState)) as IState;
    const module = state.parameter?.input[action.payload.index]?.module;
    if (module === undefined) {
      yield put(
        raiseAction(ActionType.ACT_CHANGE_INPUT_FAIL, `Illegal module ${action.payload.index}.`),
      );
      return;
    }
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.setInput,
      ioArg: {
        address: action.payload.index,
        value: action.payload.value,
      },
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_INPUT_FAIL, `Changing input failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_INPUT_FAIL, error);
    }
  }

  protected *setOutput(action: IAction<IActionChangeOutput>) {
    const state = (yield select(this.getState)) as IState;
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.setOutput,
      ioArg: {
        address: action.payload.index,
        value: action.payload.cmd,
      },
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_OUTPUT_FAIL, `Changing output failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_OUTPUT_FAIL, error);
    }
  }

  protected *ackAlarm(action: IAction<IActionAckAlarm>) {
    try {
      let result = false;
      const state = (yield select(this.getState)) as IState;
      const args: NewAlarmDataInput = {
        siteId: state.cached.selectedSite!,
        alarmTimestamp: action.payload.alarmTime / 1000,
        source: action.payload.source,
        value: action.payload.value,
        isActive: false,
        ackTimestamp: Math.round(Date.now() / 1000),
        ackSource: state.site.user?.secId ?? "enline cloud",
      };
      const apiResult = (yield this.API?.graphql({
        query: newAlarmDataGql,
        variables: { data: args },
      })) as GraphQLResult<NewAlarmDataMutation> | undefined;
      result = apiResult ? true : false;
      if (!result) {
        yield put(raiseAction(ActionType.ACT_ACK_ALARM_FAIL, `Alarm ack failed.`));
        return;
      }
      yield put(raiseAction(ActionType.ACT_ACK_ALARM_SUCC, action.payload));
      // load new state
      yield put(raiseAction(ActionType.ACT_GET_ALARMS_REQ));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_ACK_ALARM_FAIL, error);
    }
  }

  protected *changeTrendData(action: IAction<IActionChangeTrend>) {
    const state = (yield select(this.getState)) as IState;
    const tlId = state.parameter?.tariffline[action.payload.tlIndex]?.id ?? action.payload.tlIndex;
    try {
      const args: NewTrendDataInput = {
        siteId: state.cached.selectedSite!,
        tlId,
        timestamp: action.payload.timestamp,
        regperiod: action.payload.regperiod,
        energy: action.payload.energy,
        costs: action.payload.costs,
        tariff: action.payload.tariff,
        targetpower: action.payload.targetpower,
      };
      const result = (yield this.API?.graphql({
        query: newTrendDataGql,
        variables: { data: args },
      })) as GraphQLResult<NewTrendDataMutation> | undefined;
      if (result) {
        const trendRow = cloudTrend2LinxTrend({
          ...action.payload,
          power: (action.payload.energy * 3600) / action.payload.regperiod,
          tlId,
          siteId: state.cached.selectedSite!,
        });
        yield put(raiseAction(ActionType.ACT_CHANGE_TREND_SUCC, trendRow));
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_TREND_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_TREND_FAIL, error);
    }
  }

  protected *getOcppChargers(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListChargersQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listChargers,
        variables: args,
      })) as GraphQLResult<ListChargersQuery> | undefined;
      const chargerList = (result?.data?.listChargers ?? []).map((curRow) => {
        const charger: IActionOcppCharger = {
          fk_charger: parseApiInt(curRow?.id),
          chargepoint: parseApiString(curRow?.chargepoint).split("@")[0], // strip site name
          isActive: parseApiBool(curRow?.isActive),
          connectionType: (curRow?.connectionType as ChargerConnectionType | null) ?? undefined,
          // ocpp configuration data
          vendor: curRow?.vendor ?? undefined, // null -> undefined
          model: curRow?.model ?? undefined, // null -> undefined
          firmware: curRow?.firmware ?? undefined, // null -> undefined
          serial: curRow?.serial ?? undefined, // null -> undefined
          numberofconnectors: isSet(curRow?.numberofconnectors)
            ? parseApiInt(curRow?.numberofconnectors)
            : undefined,
          hasSmartCharging: parseApiBool(curRow?.hasSmartCharging),
          // user configuration data
          name: curRow?.name ?? undefined, // null -> undefined
          nominalPower: curRow?.nominalPower ?? undefined, // null -> undefined
          restrictAuth: parseApiBool(curRow?.restrictAuth),
          coordinates: curRow?.coordinates
            ? parseApiItemArray(curRow.coordinates, parseApiFloat)
            : undefined,
          tariffs: parseApiItemArray(curRow?.tariffs),
          // extended data
          lastMessage: curRow?.lastMessage ?? undefined, // null -> undefined
        };
        return charger;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_CHARGERS_SUCC, chargerList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_CHARGERS_FAIL, error);
    }
  }

  protected *saveOcppCharger(action: IAction<IActionChangeChargerSettings>) {
    const chargerReq: ModifyChargerInput = {
      id: `${action.payload.id}`,
      deleteCharger: action.payload.deleteCharger,
      name: action.payload.name,
      nominalPower: action.payload.nominalPower,
      restrictAuth: action.payload.restrictAuth,
      coordinates: action.payload.coordinates,
    };
    try {
      // first, modify charger
      const result = (yield this.API?.graphql({
        query: modifyChargerGql,
        variables: { data: chargerReq },
      })) as GraphQLResult<ModifyChargerMutation> | undefined;
      if (!result?.data.modifyCharger) {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL));
      }
      // second, modify all connectors
      if (action.payload.connector) {
        for (let curId = 0; curId < action.payload.connector.length; curId++) {
          if (action.payload.connector[curId]) {
            const connectorData = action.payload.connector[curId];
            const connectorReq: ModifyConnectorInput = {
              ...connectorData,
              chargerId: action.payload.id,
              connectorId: curId,
            };
            const result = (yield this.API?.graphql({
              query: modifyConnectorGql,
              variables: { data: connectorReq },
            })) as GraphQLResult<ModifyConnectorMutation> | undefined;
            if (!result?.data.modifyConnector) {
              yield put(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL));
            }
          }
        }
      }
      yield put(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_SUCC, action.payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL, error);
    }
  }

  protected *getOcppRfidsBasic(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListRfidsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listRfidsBasic,
        variables: args,
      })) as GraphQLResult<ListRfidsBasicQuery> | undefined;
      const rfidList = (result?.data?.listRfids ?? []).map((curRow) => {
        const rfid: IRfid = {
          id: parseApiInt(curRow?.id),
          siteId: parseApiInt(curRow?.siteId),
          rfid: parseApiString(curRow?.rfid),
          category: parseApiString(curRow?.category),
          isActive: parseApiBool(curRow?.isActive),
          name: curRow?.name ?? undefined,
          chargerRestricted: parseApiBool(curRow?.chargerRestricted),
          limitStart: curRow?.limitStart ?? undefined, // null -> undefined
          limitEnd: curRow?.limitEnd ?? undefined, // null -> undefined
          limitEnergy: curRow?.limitEnergy ?? undefined, // null -> undefined
          limitCost: curRow?.limitCost ?? undefined, // null -> undefined
          limitPeriod: (curRow?.limitPeriod as eRfidLimitPeriod | null) ?? undefined, // null -> undefined
          maxPower: curRow?.maxPower ?? undefined, // null -> undefined
          accessRights: JSON.parse(curRow?.accessRights ? curRow?.accessRights : "{}") ?? undefined, // null -> undefined
          carName: curRow?.carName ?? undefined,
          carBattery: curRow?.carBattery ?? undefined,
          additionalData: curRow?.additionalData ?? undefined, // null -> undefined
        };
        return rfid;
      }) as IActionTypeRfidList;
      yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_BASIC_SUCC, rfidList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_RFIDS_BASIC_FAIL, error);
    }
  }

  protected *getOcppRfidsExt(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListRfidsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listRfids,
        variables: args,
      })) as GraphQLResult<ListRfidsQuery> | undefined;
      const rfidList = (result?.data?.listRfids ?? []).map((curRow) => {
        const rfid: IRfid = {
          id: parseApiInt(curRow?.id),
          siteId: parseApiInt(curRow?.siteId),
          rfid: parseApiString(curRow?.rfid),
          siteIds: parseApiItemArray(curRow?.siteIds, parseApiInt),
          category: parseApiString(curRow?.category),
          isActive: parseApiBool(curRow?.isActive),
          name: curRow?.name ?? undefined,
          chargerRestricted: parseApiBool(curRow?.chargerRestricted),
          chargers: parseApiItemArray(curRow?.chargers, parseApiInt),
          tariffs: parseApiItemArray(curRow?.tariffs),
          limitStart: curRow?.limitStart ?? undefined, // null -> undefined
          limitEnd: curRow?.limitEnd ?? undefined, // null -> undefined
          limitEnergy: curRow?.limitEnergy ?? undefined, // null -> undefined
          limitCost: curRow?.limitCost ?? undefined, // null -> undefined
          limitPeriod: (curRow?.limitPeriod as eRfidLimitPeriod | null) ?? undefined, // null -> undefined
          maxPower: curRow?.maxPower ?? undefined, // null -> undefined
          accessRights: JSON.parse(curRow?.accessRights ? curRow?.accessRights : "{}") ?? undefined, // null -> undefined
          carName: curRow?.carName ?? undefined,
          carBattery: curRow?.carBattery ?? undefined,
          usedEnergy: curRow?.usedEnergy ?? undefined, // null -> undefined
          usedCost: curRow?.usedCost ?? undefined, // null -> undefined
          lastUsage: curRow?.lastUsage ?? undefined, // null -> undefined
          additionalData: curRow?.additionalData ?? undefined, // null -> undefined
        };
        return rfid;
      }) as IActionTypeRfidList;
      yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_SUCC, rfidList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_RFIDS_EXT_FAIL, error);
    }
  }

  protected *checkOcppRfid(action: IAction<string>) {
    const args: CheckRfidQueryVariables = { rfid: action.payload };
    try {
      const result = (yield this.API?.graphql({
        query: checkRfid,
        variables: args,
      })) as GraphQLResult<CheckRfidQuery> | undefined;
      const check = result?.data?.checkRfid ?? undefined;
      yield put(raiseAction(ActionType.ACT_CHECK_OCPP_RFIDS_SUCC, check));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHECK_OCPP_RFIDS_FAIL, error);
    }
  }

  protected *saveOcppRfid(action: IAction<IActionChangeRfid>) {
    const state = (yield select(this.getState)) as IState;
    const { id, isLoginAllowed, isChargerPowerCtrlAllowed, ...data } = action.payload;
    const args: ModifyRfidInput = {
      id: isSet(id) ? `${id}` : undefined,
      siteId: state.cached.selectedSite!,
      ...data,
      accessRights: JSON.stringify({ isLoginAllowed, isChargerPowerCtrlAllowed }),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifyRfidGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyRfidMutation> | undefined;
      if (result?.data.modifyRfid) {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_SUCC, action.payload));
        // reload rfid data
        yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ));
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_RFID_FAIL, error);
    }
  }

  protected *getOcppRefusedRfids(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListRefusedRfidsQueryVariables = {
      siteId: state.cached.selectedSite!,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listRefusedRfids,
        variables: args,
      })) as GraphQLResult<ListRefusedRfidsQuery> | undefined;
      const rfidList = (result?.data?.listRefusedRfids ?? []).map((curRow) => {
        const rfid: IRefusedRfid = {
          charger: parseApiInt(curRow?.charger),
          rfid: parseApiString(curRow?.rfid),
          firstAttempt: parseApiInt(curRow?.firstAttempt),
          lastAttempt: parseApiInt(curRow?.lastAttempt),
          counter: parseApiInt(curRow?.counter),
        };
        return rfid;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_REFUSED_RFIDS_SUCC, rfidList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_REFUSED_RFIDS_FAIL, error);
    }
  }

  protected *getOcppTariffs(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListTariffsQueryVariables = {
      siteId: state.site.user?.isRfidUser ? undefined : state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listTariffs,
        variables: args,
      })) as GraphQLResult<ListTariffsQuery> | undefined;
      const tariffList = (result?.data?.listTariffs ?? []).map((curRow) => {
        const tariff: ITariff = {
          id: parseApiInt(curRow?.id),
          siteId: isSet(curRow?.siteId) ? parseApiInt(curRow?.siteId) : undefined,
          name: parseApiString(curRow?.name),
          currency: parseApiString(curRow?.currency),
          isActive: isSet(curRow?.isActive) ? parseApiBool(curRow!.isActive) : undefined,
          priority: isSet(curRow?.priority) ? parseApiInt(curRow!.priority) : undefined,
          costPerEnergy: curRow?.costPerEnergy ?? undefined, // null -> undefined
          costPerPeriod: curRow?.costPerPeriod ?? undefined, // null -> undefined
          validFrom: curRow?.validFrom ?? undefined, // null -> undefined
          validUntil: curRow?.validUntil ?? undefined, // null -> undefined
          timeStart: curRow?.timeStart ?? undefined, // null -> undefined
          timeEnd: curRow?.timeEnd ?? undefined, // null -> undefined
        };
        return tariff;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SITE_TARIFFS_SUCC, tariffList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_SITE_TARIFFS_FAIL, error);
    }
  }

  protected *saveOcppTariff(action: IAction<IActionChangeTariff>) {
    const state = (yield select(this.getState)) as IState;
    const { id, ...data } = action.payload;
    const args: ModifyTariffInput = {
      id: isSet(id) ? `${id}` : undefined,
      siteId: state.cached.selectedSite!,
      ...data,
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifyTariffGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyTariffMutation> | undefined;
      if (result?.data.modifyTariff) {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_SUCC, action.payload));
        // reload tariff data, depending on modification
        if (isSet(args.rfidId)) {
          yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ));
        } else if (isSet(args.chargerId)) {
          yield put(raiseAction(ActionType.ACT_GET_OCPP_CHARGERS_REQ));
        } else {
          yield put(raiseAction(ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ));
        }
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_TARIFF_FAIL, error);
    }
  }

  protected *getOcppConnectors(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListConnectorsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listConnectors,
        variables: args,
      })) as GraphQLResult<ListConnectorsQuery> | undefined;
      const connectorList = (result?.data?.listConnectors ?? []).map((curRow) => {
        const connector: IActionOcppConnector = {
          fk_charger: parseApiInt(curRow?.chargerId),
          connector: parseApiInt(curRow?.connectorId),
          // user configuration data
          lastChange: parseApiFloat(curRow?.lastUpdate),
          name: curRow?.name ?? undefined, // null -> undefined
          evseid: curRow?.evseid ?? undefined, // null -> undefined
          plugType: curRow?.plugType ?? undefined, // null -> undefined
          nominalPower: curRow?.nominalPower ?? undefined, // null -> undefined
          isDc: curRow?.isDc ?? undefined, // null -> undefined
          nrPhases: curRow?.nrPhases ?? undefined, // null -> undefined
          // ocpp data
          lastUpdate: parseApiFloat(curRow?.lastUpdate),
          power: curRow?.power ?? undefined,
          current: parseApiItemArray(curRow?.current, (item) => item ?? 0),
          voltage:
            curRow?.voltage && curRow.voltage.length
              ? parseApiItemArray(curRow.voltage, (item) => item ?? 0)
              : [],
          sessionEnergy: curRow?.sessionenergy ?? undefined, // null -> undefined
          energymeter: curRow?.energymeter ?? undefined, // null -> undefined
          connectorStatus: (curRow?.connectorStatus as ConnectorStatus | null) ?? undefined,
          connectorStatusTimestamp: parseApiFloat(curRow?.connectorStatusTimestamp),
        };
        return connector;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_CONNECTORS_SUCC, connectorList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_CONNECTORS_FAIL, error);
    }
  }

  protected *getOcppSessions(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    // when filtering ocpp sessions
    // - charger/connector/rfid is taken from state.router
    // - the rest is taken from state.gui.chargingSessionFilter
    const charger = state.ocpp.charger.find(
      (curItem) => curItem.fk_charger === state.router.charger,
    );
    const rfid = state.ocpp.rfid.find((curItem) => curItem.id === state.router.rfid);
    // if we selected a charger, it must be an OCPP charger
    if (isSet(charger) && charger.fk_charger === undefined) {
      // non-OCPP charger; return empty session list
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_SUCC, []));
      return;
    }
    // no charger, or OCPP charger; setup query filters
    const graphDates = getGraphBoundaryDates(
      state.gui.chargingSessionFilter.startDate,
      state.gui.chargingSessionFilter.endDate,
      state.site.tzOffset,
      state.gui.chargingSessionFilter.timePeriod ?? TimePeriod.MONTH,
    );
    const args: ListChargingSessionsQueryVariables = {
      siteId: state.cached.selectedSite,
      chargerId: charger ? charger.fk_charger : undefined,
      connector: state.router.connector,
      startDate: graphDates.startDate,
      endDate: graphDates.endDate,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : rfid?.rfid,
      user: state.gui.chargingSessionFilter.user,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listChargingSessions,
        variables: args,
      })) as GraphQLResult<ListChargingSessionsQuery> | undefined;
      const chargerList = (result?.data?.listChargingSessions ?? []).map((curRow) => {
        const chargerSession: IOcppChargingSession = {
          id: parseApiInt(curRow?.id),
          fk_charger: parseApiInt(curRow?.chargerId),
          fk_tariff: curRow?.tariffId ?? undefined,
          startTimestamp: parseApiInt(curRow?.startDate),
          stopTimestamp: curRow?.stopDate ? parseApiInt(curRow?.stopDate) : undefined,
          connector: parseApiInt(curRow?.connector),
          rfid: curRow?.rfid ? parseApiString(curRow?.rfid) : undefined,
          rfidId: curRow?.rfidId ? parseApiInt(curRow?.rfidId) : undefined,
          startMeter: parseApiInt(curRow?.startMeter),
          stopMeter: curRow?.stopDate ? parseApiInt(curRow?.stopMeter) : undefined,
          stopReason: curRow?.stopReason ? (curRow.stopReason as StopReason) : undefined,
          costs: isSet(curRow?.costs) ? parseApiFloat(curRow!.costs) : undefined,
        };
        return chargerSession;
      }) as IActionTypeChargingSessionList;
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_SUCC, chargerList));
      // as we got new session data, we might face a new pending session
      // in this case we must renew out livedata subscriptions as an RFID user only
      // subscribes to pending sessions
      if (!state.scheduler.isInBackground) {
        yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ, false));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_SESSIONS_FAIL, error);
    }
  }

  protected *getPendingOcppSessions(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListChargingSessionsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
      endDate: 0,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listChargingSessions,
        variables: args,
      })) as GraphQLResult<ListChargingSessionsQuery> | undefined;
      const sessionList = (result?.data?.listChargingSessions ?? []).map((curRow) => {
        const session: IOcppChargingSession = {
          id: parseApiInt(curRow?.id),
          fk_charger: parseApiInt(curRow?.chargerId),
          fk_tariff: curRow?.tariffId ?? undefined,
          startTimestamp: parseApiInt(curRow?.startDate),
          connector: parseApiInt(curRow?.connector),
          rfid: curRow?.rfid ? parseApiString(curRow?.rfid) : undefined,
          rfidId: curRow?.rfidId ? parseApiInt(curRow?.rfidId) : undefined,
          startMeter: parseApiInt(curRow?.startMeter),
        };
        return session;
      }) as IActionTypeChargingSessionList;
      // potentially also subscribe to session connector data
      if (state.site.user?.isRfidUser) {
        for (let i = 0; i < sessionList.length; i++) {
          yield this.liveDataHandler?.subscribeChargerDataForConnector(
            sessionList[i].fk_charger,
            sessionList[i].connector,
            true,
          );
        }
      }
      yield put(raiseAction(ActionType.ACT_GET_PENDING_OCPP_SESSIONS_SUCC, sessionList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_PENDING_OCPP_SESSIONS_FAIL, error);
    }
  }

  protected *getChargingSession(action: IAction<number>) {
    const state = (yield select(this.getState)) as IState;
    const transactionId = action.payload;
    let session = state.ocpp.chargingSession.find((curItem) => curItem.id === transactionId);
    // if session is not found, also look in pending session list
    if (session === undefined) {
      session = state.ocpp.pendingChargingSession.find((curItem) => curItem.id === transactionId);
    }
    if (!session) {
      console.error("no charging session to get");
      return;
    }
    const siteId = state.site.user?.isRfidUser ? undefined : state.cached.selectedSite;
    const rfid = state.site.user?.isRfidUser ? state.site.user.secId : undefined;
    // run all these query in parallel
    yield all({
      states: call(this.getChargingSessionStates.bind(this, session, Boolean(state.site.user))),
      trending: call(this.getChargingSessionTrending.bind(this, session, siteId, rfid)),
      control: call(this.getChargingSessionControl.bind(this, session, transactionId)),
      tariffs: call(this.getChargingSessionTariffs.bind(this, session, siteId, rfid)),
    });
  }

  private *getChargingSessionStates(session: IOcppChargingSession, isRfidUser: boolean) {
    const stateArgs: ListChargerStatusNotificationsQueryVariables = {
      chargerId: session?.fk_charger ?? 0,
      // rfid users only get data of their own connector
      connector: isRfidUser ? session?.connector : undefined,
      // we query all connectors to include charger station state events
      // we extend the time period slightly to catch the start and stop event
      startDate: session.startTimestamp ? session.startTimestamp - 10 : undefined,
      endDate: session.stopTimestamp ? session.stopTimestamp + 10 : undefined,
    };
    // the state events
    try {
      const result = (yield this.API?.graphql({
        query: listChargerStatusNotifications,
        variables: stateArgs,
      })) as GraphQLResult<ListChargerStatusNotificationsQuery> | undefined;
      const stateList = (result?.data?.listChargerStatusNotifications ?? []).map((curRow) => {
        const stateEvent: IChargerStateEvent = {
          id: parseApiInt(curRow?.id),
          fk_charger: parseApiInt(curRow?.chargerId),
          connector: parseApiInt(curRow?.connector),
          timestamp: parseApiInt(curRow?.timestamp),
          source: curRow.source,
          statusCode: (curRow?.statusCode as ConnectorStatus) ?? ConnectorStatus.UNAVAILABLE,
          errorCode: parseApiString(curRow?.errorCode),
          info: curRow?.info ? parseApiString(curRow?.info) : undefined,
        };
        return stateEvent;
      }) as IActionTypeChargerStateChangeList;
      yield put(raiseAction(ActionType.ACT_GET_CHARGING_STATES_SUCC, stateList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_CHARGING_STATES_FAIL, error);
    }
  }

  private *getChargingSessionTrending(
    session: IOcppChargingSession,
    siteId: number | undefined,
    rfid: string | undefined,
  ) {
    // get trending data
    const trendArgs: ListTrendDataQueryVariables = {
      siteId: siteId ?? 0, // not used for RFID users
      rfid,
      chargerId: session?.fk_charger,
      startDate: session?.startTimestamp,
      endDate: session?.stopTimestamp,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listTrendDataGql,
        variables: trendArgs,
      })) as GraphQLResult<ListTrendDataQuery> | undefined;
      // convert json string to object
      const data: IActionCloudTrendDataRow[] =
        result?.data?.listTrendData?.map((curData) => {
          return cloudTrend2LinxTrend(curData!);
        }) ?? [];
      yield put(raiseAction(ActionType.ACT_GET_CHARGERTREND_SUCC, data));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_TREND_FAIL, error);
    }
  }

  private *getChargingSessionControl(session: IOcppChargingSession, transactionId: number) {
    // charging control data
    const controlArgs: GetChargerControlQueryVariables = {
      chargerId: session?.fk_charger,
      transactionId,
    };
    try {
      const result = (yield this.API?.graphql({
        query: getChargerControl,
        variables: controlArgs,
      })) as GraphQLResult<GetChargerControlQuery> | undefined;
      yield put(
        raiseAction(ActionType.ACT_GET_CHARGER_POWER_SUCC, result?.data?.getChargerControl),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_CHARGER_POWER_FAIL, error);
    }
  }

  private *getChargingSessionTariffs(
    session: IOcppChargingSession,
    siteId: number | undefined,
    rfid: string | undefined,
  ) {
    // cost tariffs
    const tariffArgs: ListTariffsQueryVariables = {
      siteId,
      rfid,
      startDate: session?.startTimestamp ? session.startTimestamp - 10 : undefined,
      endDate: session?.stopTimestamp ? session?.stopTimestamp + 10 : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listTariffs,
        variables: tariffArgs,
      })) as GraphQLResult<ListTariffsQuery> | undefined;
      const tariffList = (result?.data?.listTariffs ?? []).map((curRow) => {
        const tariff: ITariff = {
          id: parseApiInt(curRow?.id),
          siteId: isSet(curRow?.siteId) ? parseApiInt(curRow?.siteId) : undefined,
          name: parseApiString(curRow?.name),
          currency: parseApiString(curRow?.currency),
          isActive: parseApiBool(curRow?.isActive),
          priority: parseApiInt(curRow?.priority),
          costPerEnergy: curRow?.costPerEnergy ?? undefined, // null -> undefined
          costPerPeriod: curRow?.costPerPeriod ?? undefined, // null -> undefined
          validFrom: curRow?.validFrom ?? undefined, // null -> undefined
          validUntil: curRow?.validUntil ?? undefined, // null -> undefined
          timeStart: curRow?.timeStart ?? undefined, // null -> undefined
          timeEnd: curRow?.timeEnd ?? undefined, // null -> undefined
        };
        return tariff;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSION_TARIFFS_SUCC, tariffList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_SESSION_TARIFFS_FAIL, error);
    }
  }

  protected *newChargingSession(action: IAction<IActionNewChargingSessionData>) {
    // in case of rfid users, we need to subscribe for live updates of a new session
    // or unsubscribe for closed sessions
    const state = (yield select(this.getState)) as IState;
    if (state.site.user?.isRfidUser) {
      yield this.liveDataHandler?.subscribeChargerDataForConnector(
        action.payload.chargerId,
        action.payload.connector,
        !isSet(action.payload.stopDate),
      );
    }
  }

  protected *triggerChargerCommand(action: IAction<IActionChargerCommand>) {
    const args: TriggerChargerCommandMutationVariables = {
      chargerId: action.payload.chargerId,
      command: action.payload.cmd,
      args: action.payload.cmdArgs ? JSON.stringify(action.payload.cmdArgs) : undefined,
      seqNr: makeCommandSeqNr(action.payload.timestampMs),
    };
    try {
      const result = (yield this.API?.graphql({
        query: triggerChargerCommandGql,
        variables: args,
      })) as GraphQLResult<TriggerChargerCommandMutation> | undefined;
      if (result?.data.triggerChargerCommand) {
        yield put(raiseAction(ActionType.ACT_TRIGGER_CHARGER_CMD_SUCC));
      } else {
        yield put(raiseAction(ActionType.ACT_TRIGGER_CHARGER_CMD_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_TRIGGER_CHARGER_CMD_FAIL, error);
    }
  }

  protected *saveChargerPower(action: IAction<IActionChargingPower>) {
    const { chargerId, transactionId, mode, ...data } = action.payload;
    const args: ModifyChargerControlMutationVariables = {
      chargerId,
      transactionId,
      data: {
        ...data,
        mode: mode as string as ApiChargingMode,
      },
    };
    try {
      const chargerResult = (yield this.API?.graphql({
        query: modifyChargerControlGql,
        variables: args,
      })) as GraphQLResult<ModifyChargerControlMutation>;
      if (!chargerResult?.data.modifyChargerControl) {
        yield put(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_FAIL));
        return;
      }
      // if we modified an ongoing session (not default control),
      // also trigger ocpp command to immediate react on new settings
      let cmdResult: GraphQLResult<TriggerChargerCommandMutation> | undefined;
      if (chargerResult && transactionId) {
        const cmdArgs: TriggerChargerCommandMutationVariables = {
          chargerId,
          command: ChargerCommand.TriggerPowerControl,
          args: JSON.stringify({ transactionId }),
          seqNr: makeCommandSeqNr(),
        };
        cmdResult = (yield this.API?.graphql({
          query: triggerChargerCommandGql,
          variables: cmdArgs,
        })) as GraphQLResult<TriggerChargerCommandMutation> | undefined;
      }
      if (cmdResult?.data.triggerChargerCommand) {
        yield put(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_SUCC, action.payload));
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_FAIL, "failed to trigger"));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_CHARGER_POWER_FAIL, error);
    }
  }

  protected *openSshTunnel(action: IAction<IActionCommand>) {
    const state = (yield select(this.getState)) as IState;
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.openSshTunnel,
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_OPEN_SSH_TUNNEL_FAIL, `Open tunnel failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_OPEN_SSH_TUNNEL_FAIL, error);
    }
  }

  protected *doReboot(action: IAction<IActionCommand>) {
    const state = (yield select(this.getState)) as IState;
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.rebootController,
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_REBOOT_FAIL, `Controller reboot failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_REBOOT_FAIL, error);
    }
  }

  protected *performFileUpload(action: IAction<IActionImporterFileUpload>) {
    const state = (yield select(this.getState)) as IState;
    const args: ImportFileInput = {
      siteId: state.cached.selectedSite!,
      fileType: action.payload.fileType,
      args: JSON.stringify(action.payload.args),
    };
    const file = action.payload.file;
    console.assert(file, "No upload file");

    // first, get pre-signed url to upload the file to
    let url: string | undefined;
    try {
      const result = (yield this.API?.graphql({
        query: importFileGql,
        variables: { data: args },
      })) as GraphQLResult<ImportFileMutation> | undefined;
      if (result && !result.errors) {
        url = result.data.importFile?.url;
      }
      if (!isSet(url)) {
        yield put(
          raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, "No URL received for Upload!"),
        );
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, error);
    }

    // Use fetch to upload the file to the presigned URL
    if (isSet(url)) {
      try {
        const upload_result = (yield fetch(url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        })) as Response;
        if (upload_result.ok) {
          yield put(raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_SUCC));
        } else {
          yield put(raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, upload_result));
        }
        // initial requst of current file-uploads
        yield put(raiseAction(ActionType.ACT_GET_FILEUPLOADS_REQ));
      } catch (error) {
        yield this.raiseHttpError(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, error);
      }
    }
  }

  protected *getFileUploads(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListImportFilesQueryVariables = { siteId: state.cached.selectedSite! };
    try {
      const response = (yield this.API?.graphql({
        query: listImportFiles,
        variables: args,
      })) as GraphQLResult<ListImportFilesQuery> | undefined;
      yield put(raiseAction(ActionType.ACT_GET_FILEUPLOADS_SUCC, response?.data.listImportFiles));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_FILEUPLOADS_FAIL, error);
    }
  }
}
